/**
 * "Pages this organisation can view" card on the Users page org drill-down.
 *
 * Rows come from two places:
 *   - the code baseline (which org blocks in router.tsx mount the page),
 *     read from the generated catalogue (src/access/pageCatalogue.json);
 *   - override rows in `dashboard_org_pages` for this org (allow / block).
 *
 * Actions write override rows through the admin endpoints; the backend
 * applies them to the API allow-list and `/init` feeds them back to the
 * router and sidebar, so no code change is needed to grant or remove a page.
 *
 *   Add page    → effect=allow, api_paths = every endpoint the page uses
 *   Remove      → baseline page: effect=block, api_paths = endpoints only
 *                 this page uses (shared ones keep working for the rest);
 *                 added page: the allow row is deleted
 *   Restore     → the block row is deleted
 *   Sync        → re-snapshot api_paths when the catalogue has moved on
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Loading } from 'notiflix'
import api from '../../../helpers/apiRequester'
import {
    OVERRIDE_EXEMPT_ORGANISATIONS,
    PROTECTED_PAGES,
    exclusiveEndpoints,
    isBaselinePage,
    pageEndpoints,
} from '../../../access/pageCatalogue'
import {
    ORGANISATION_PAGE_NOTES,
    OrganisationPage,
    getAllPages,
    getBaselinePages,
    isKnownOrganisation,
    sortPages,
    toOrganisationPage,
} from '../../../access/pageMeta'
import styles from './styles.module.scss'

interface Override {
    organisation: string
    pagePath: string
    effect: 'allow' | 'block'
    apiPaths: string[]
    setBy: string | null
    setAt: string | null
}

type RowStatus = 'baseline' | 'added' | 'blocked'

interface PageRow extends OrganisationPage {
    status: RowStatus
    override?: Override
    /** Override api_paths no longer match what the catalogue says the page needs. */
    stale: boolean
}

interface Props {
    organisation: string
    label: string
}

const same = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false
    const sa = [...a].sort()
    const sb = [...b].sort()
    return sa.every((v, i) => v === sb[i])
}

const formatWhen = (iso: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const EndpointList = ({ endpoints }: { endpoints: string[] }) => (
    <ul className={styles.endpointList}>
        {endpoints.map((e) => (
            <li key={e}>
                <code className={styles.pathCode}>{e}</code>
            </li>
        ))}
    </ul>
)

export const OrgPagesCard = ({ organisation, label }: Props) => {
    const [overrides, setOverrides] = useState<Override[]>([])
    const [loadingOverrides, setLoadingOverrides] = useState(false)
    const [busyPath, setBusyPath] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [pickerOpen, setPickerOpen] = useState(false)
    const [pickerSearch, setPickerSearch] = useState('')
    const [expandedPath, setExpandedPath] = useState<string | null>(null)

    const exempt = OVERRIDE_EXEMPT_ORGANISATIONS.includes(organisation)
    const known = isKnownOrganisation(organisation)

    const loadOverrides = useCallback(async () => {
        if (exempt) return
        setLoadingOverrides(true)
        try {
            const res = await api({ url: 'org-pages-list', data: { organisation } })
            setOverrides(res?.data?.success ? res.data.overrides || [] : [])
        } catch (error: any) {
            console.error('Failed to load page overrides:', error)
            setOverrides([])
        } finally {
            setLoadingOverrides(false)
        }
    }, [organisation, exempt])

    useEffect(() => {
        setSearch('')
        setPickerOpen(false)
        setPickerSearch('')
        setExpandedPath(null)
        loadOverrides()
    }, [loadOverrides])

    // ── Derived rows ───────────────────────────────────────────────────────
    const { rows, visiblePaths } = useMemo(() => {
        const byPath = new Map<string, Override>()
        overrides.forEach((o) => byPath.set(o.pagePath, o))
        const baseline = getBaselinePages(organisation)
        const baselinePaths = new Set(baseline.map((p) => p.path))

        // What the org can currently reach: baseline minus blocks, plus allows.
        const visible = new Set<string>()
        baseline.forEach((p) => {
            if (byPath.get(p.path)?.effect !== 'block') visible.add(p.path)
        })
        overrides.forEach((o) => {
            if (o.effect === 'allow') visible.add(o.pagePath)
        })
        const visibleList = Array.from(visible)

        const expectedApiPaths = (o: Override) =>
            o.effect === 'allow'
                ? pageEndpoints(o.pagePath)
                : exclusiveEndpoints(
                      o.pagePath,
                      visibleList.filter((p) => p !== o.pagePath),
                  )

        const built: PageRow[] = baseline.map((p) => {
            const o = byPath.get(p.path)
            if (o?.effect === 'block') {
                return { ...p, status: 'blocked', override: o, stale: !same(o.apiPaths, expectedApiPaths(o)) }
            }
            return { ...p, status: 'baseline', stale: false }
        })
        overrides
            .filter((o) => o.effect === 'allow' && !baselinePaths.has(o.pagePath))
            .forEach((o) => {
                built.push({
                    ...toOrganisationPage(o.pagePath, false),
                    status: 'added',
                    override: o,
                    stale: !same(o.apiPaths, expectedApiPaths(o)),
                })
            })
        return { rows: sortPages(built), visiblePaths: visibleList }
    }, [organisation, overrides])

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return rows
        return rows.filter(
            (r) =>
                r.name.toLowerCase().includes(term) ||
                r.path.toLowerCase().includes(term) ||
                r.section.toLowerCase().includes(term) ||
                r.status.includes(term),
        )
    }, [rows, search])

    const candidates = useMemo(() => {
        const visible = new Set(visiblePaths)
        const term = pickerSearch.trim().toLowerCase()
        return getAllPages().filter(
            (p) =>
                !visible.has(p.path) &&
                !isBaselinePage(organisation, p.path) &&
                (!term ||
                    p.name.toLowerCase().includes(term) ||
                    p.path.toLowerCase().includes(term) ||
                    p.section.toLowerCase().includes(term)),
        )
    }, [organisation, visiblePaths, pickerSearch])

    const counts = useMemo(
        () => ({
            visible: rows.filter((r) => r.status !== 'blocked').length,
            added: rows.filter((r) => r.status === 'added').length,
            blocked: rows.filter((r) => r.status === 'blocked').length,
            stale: rows.filter((r) => r.stale).length,
        }),
        [rows],
    )

    // ── Mutations ──────────────────────────────────────────────────────────
    const setOverride = async (pagePath: string, effect: 'allow' | 'block', apiPaths: string[], verb: string) => {
        setBusyPath(pagePath)
        Loading.circle(`${verb}...`)
        try {
            const res = await api({ url: 'org-pages-set', data: { organisation, pagePath, effect, apiPaths } })
            if (!res?.data?.success) {
                Report.failure('Could not save', res?.data?.message || 'The page override was not saved.', 'Okay')
                return
            }
            const ignored: string[] = res.data.ignored || []
            if (ignored.length) {
                Report.info(
                    'Saved with a note',
                    `${ignored.length} endpoint${ignored.length === 1 ? '' : 's'} referenced by this page ${
                        ignored.length === 1 ? 'is' : 'are'
                    } not registered on the API and ${ignored.length === 1 ? 'was' : 'were'} skipped: ${ignored.join(', ')}`,
                    'Okay',
                )
            }
            await loadOverrides()
        } catch (error: any) {
            Report.failure(
                'Could not save',
                error?.response?.data?.message || error?.message || 'Request failed.',
                'Okay',
            )
        } finally {
            Loading.remove()
            setBusyPath(null)
        }
    }

    const clearOverride = async (pagePath: string, verb: string) => {
        setBusyPath(pagePath)
        Loading.circle(`${verb}...`)
        try {
            const res = await api({ url: 'org-pages-clear', data: { organisation, pagePath } })
            if (!res?.data?.success) {
                Report.failure('Could not save', res?.data?.message || 'The page override was not removed.', 'Okay')
                return
            }
            await loadOverrides()
        } catch (error: any) {
            Report.failure(
                'Could not save',
                error?.response?.data?.message || error?.message || 'Request failed.',
                'Okay',
            )
        } finally {
            Loading.remove()
            setBusyPath(null)
        }
    }

    const handleAdd = (page: OrganisationPage) => {
        const endpoints = pageEndpoints(page.path)
        Confirm.show(
            `Add ${page.name}`,
            `${label} will be able to open ${page.path}${
                endpoints.length
                    ? ` and call ${endpoints.length} API endpoint${endpoints.length === 1 ? '' : 's'} it uses.`
                    : '. This page makes no API calls.'
            }`,
            'Add page',
            'Cancel',
            () => {
                setPickerOpen(false)
                setOverride(page.path, 'allow', endpoints, `Adding ${page.name}`)
            },
        )
    }

    const handleRemove = (row: PageRow) => {
        if (row.status === 'added') {
            Confirm.show(
                `Remove ${row.name}`,
                `${label} will no longer be able to open ${row.path} or call the endpoints that were granted with it.`,
                'Remove page',
                'Cancel',
                () => clearOverride(row.path, `Removing ${row.name}`),
            )
            return
        }
        const cut = exclusiveEndpoints(
            row.path,
            visiblePaths.filter((p) => p !== row.path),
        )
        const shared = pageEndpoints(row.path).length - cut.length
        Confirm.show(
            `Remove ${row.name}`,
            `${label} will no longer be able to open ${row.path}. ${
                cut.length
                    ? `${cut.length} API endpoint${cut.length === 1 ? '' : 's'} only this page uses will be switched off`
                    : 'No API endpoints will be switched off'
            }${shared > 0 ? `; ${shared} shared with other pages stay on` : ''}.`,
            'Remove page',
            'Cancel',
            () => setOverride(row.path, 'block', cut, `Removing ${row.name}`),
        )
    }

    const handleRestore = (row: PageRow) => clearOverride(row.path, `Restoring ${row.name}`)

    const handleSync = (row: PageRow) => {
        if (!row.override) return
        const next =
            row.override.effect === 'allow'
                ? pageEndpoints(row.path)
                : exclusiveEndpoints(
                      row.path,
                      visiblePaths.filter((p) => p !== row.path),
                  )
        setOverride(row.path, row.override.effect, next, `Syncing ${row.name}`)
    }

    // ── Render ─────────────────────────────────────────────────────────────
    const statusBadge = (row: PageRow) => {
        if (row.status === 'added') return <span className={`${styles.pageBadge} ${styles.pageBadgeAdded}`}>Added</span>
        if (row.status === 'blocked')
            return <span className={`${styles.pageBadge} ${styles.pageBadgeBlocked}`}>Removed</span>
        return null
    }

    const actions = (row: PageRow) => {
        if (exempt) return null
        const busy = busyPath === row.path
        if (row.status === 'blocked') {
            return (
                <button
                    className={`${styles.actionButton} ${styles.enable}`}
                    disabled={busy}
                    onClick={() => handleRestore(row)}
                >
                    Restore
                </button>
            )
        }
        if (PROTECTED_PAGES.includes(row.path) && row.status === 'baseline') {
            return <span className={styles.pageProtected}>Always on</span>
        }
        return (
            <>
                {row.stale && (
                    <button className={styles.actionButton} disabled={busy} onClick={() => handleSync(row)}>
                        Sync
                    </button>
                )}
                <button
                    className={`${styles.actionButton} ${styles.disable}`}
                    disabled={busy}
                    onClick={() => handleRemove(row)}
                >
                    Remove
                </button>
            </>
        )
    }

    return (
        <div className={`${styles.resultsContainer} ${styles.pagesCard}`}>
            <div className={styles.resultsHeader}>
                <h3>Pages {label} can view</h3>
                <div className={styles.headerActions}>
                    <div className={styles.searchWrapper}>
                        <input
                            className={styles.searchInput}
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter pages by name, URL, section or status..."
                            aria-label="Filter pages"
                        />
                    </div>
                    {!exempt && (
                        <button
                            className={`${styles.toggleButton} ${pickerOpen ? styles.active : ''}`}
                            onClick={() => setPickerOpen((v) => !v)}
                        >
                            {pickerOpen ? 'Close' : '+ Add page'}
                        </button>
                    )}
                </div>
            </div>

            {!known && !exempt ? (
                <div className={styles.pagesIntro}>
                    This organisation is not in the dashboard router or the backend route allow-list, so by default its
                    users only get the common account pages. Use <strong>Add page</strong> to grant pages without a code
                    change.
                </div>
            ) : (
                ORGANISATION_PAGE_NOTES[organisation] && (
                    <div className={styles.pagesIntro}>{ORGANISATION_PAGE_NOTES[organisation]}</div>
                )
            )}

            {pickerOpen && (
                <div className={styles.pagePicker}>
                    <div className={styles.pagePickerHeader}>
                        <div>
                            <h4>Add a page to {label}</h4>
                            <p>
                                Pick a page. The endpoints it calls are granted with it; everything else about the page
                                (admin-only buttons, server-side scoping) is unchanged.
                            </p>
                        </div>
                        <input
                            className={styles.searchInput}
                            type="search"
                            autoFocus
                            value={pickerSearch}
                            onChange={(e) => setPickerSearch(e.target.value)}
                            placeholder="Search pages..."
                            aria-label="Search pages to add"
                        />
                    </div>
                    <div className={styles.pagePickerList}>
                        {candidates.length === 0 ? (
                            <div className={styles.noResults}>No pages match</div>
                        ) : (
                            candidates.map((p) => (
                                <button
                                    key={p.path}
                                    type="button"
                                    className={styles.pagePickerItem}
                                    disabled={busyPath !== null}
                                    onClick={() => handleAdd(p)}
                                >
                                    <span className={styles.pagePickerName}>
                                        {p.name}
                                        <span className={styles.pagePickerSection}>{p.section}</span>
                                    </span>
                                    <code className={styles.pathCode}>{p.path}</code>
                                    <span className={styles.pagePickerMeta}>
                                        {p.endpoints.length} endpoint{p.endpoints.length === 1 ? '' : 's'}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}

            <div className={styles.tableWrapper}>
                <table className={`${styles.resultsTable} ${styles.pagesTable}`}>
                    <thead>
                        <tr>
                            <th>Page</th>
                            <th>URL</th>
                            <th>Section</th>
                            <th>Endpoints</th>
                            <th>Notes</th>
                            {!exempt && <th className={styles.pageActionsHead}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.length > 0 ? (
                            filteredRows.map((row) => {
                                const expanded = expandedPath === row.path
                                return (
                                    <Fragment key={row.path}>
                                        <tr className={row.status === 'blocked' ? styles.pageRowBlocked : ''}>
                                            <td>
                                                <span className={styles.pageNameCell}>
                                                    {row.name}
                                                    {statusBadge(row)}
                                                    {row.stale && (
                                                        <span
                                                            className={`${styles.pageBadge} ${styles.pageBadgeStale}`}
                                                            title="The endpoints this page uses have changed since the override was saved. Sync to update."
                                                        >
                                                            Stale
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td>
                                                <code className={styles.pathCode}>{row.path}</code>
                                            </td>
                                            <td className={styles.sectionCell}>{row.section}</td>
                                            <td className={styles.sectionCell}>
                                                {row.endpoints.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        className={styles.linkButton}
                                                        onClick={() => setExpandedPath(expanded ? null : row.path)}
                                                    >
                                                        {row.endpoints.length} {expanded ? '▾' : '▸'}
                                                    </button>
                                                ) : (
                                                    '0'
                                                )}
                                            </td>
                                            <td className={styles.noteCell}>
                                                {row.note || ''}
                                                {row.override && (
                                                    <span className={styles.pageOverrideMeta}>
                                                        {row.note ? ' · ' : ''}
                                                        {row.status === 'added' ? 'Added' : 'Removed'}
                                                        {row.override.setBy ? ` by ${row.override.setBy}` : ''}
                                                        {row.override.setAt
                                                            ? ` on ${formatWhen(row.override.setAt)}`
                                                            : ''}
                                                    </span>
                                                )}
                                            </td>
                                            {!exempt && (
                                                <td>
                                                    <div className={styles.actionsCell}>{actions(row)}</div>
                                                </td>
                                            )}
                                        </tr>
                                        {expanded && (
                                            <tr className={styles.pageEndpointsRow}>
                                                <td colSpan={exempt ? 5 : 6}>
                                                    {row.override && row.override.effect === 'block' ? (
                                                        <>
                                                            <div className={styles.pageEndpointsTitle}>
                                                                Switched off for {label} ({row.override.apiPaths.length}
                                                                )
                                                            </div>
                                                            {row.override.apiPaths.length ? (
                                                                <EndpointList endpoints={row.override.apiPaths} />
                                                            ) : (
                                                                <span className={styles.userSearchHint}>
                                                                    None — every endpoint this page uses is shared with
                                                                    a page {label} still has.
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className={styles.pageEndpointsTitle}>
                                                                API endpoints this page uses ({row.endpoints.length})
                                                            </div>
                                                            <EndpointList endpoints={row.endpoints} />
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                )
                            })
                        ) : (
                            <tr>
                                <td colSpan={exempt ? 5 : 6} className={styles.noResults}>
                                    {rows.length === 0
                                        ? 'This organisation has no pages yet'
                                        : 'No pages match your filter'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={styles.tableFooter}>
                <span className={styles.resultsCount}>
                    {counts.visible} page{counts.visible !== 1 ? 's' : ''} visible
                    {counts.added > 0 && ` · ${counts.added} added`}
                    {counts.blocked > 0 && ` · ${counts.blocked} removed`}
                    {counts.stale > 0 && ` · ${counts.stale} stale`}
                    {loadingOverrides && ' · refreshing…'}
                </span>
                <span className={styles.userSearchHint}>
                    {exempt
                        ? 'Sentinel sees every page; overrides are not applied to it.'
                        : 'Baseline comes from the dashboard router. Added and removed pages take effect on the user’s next sign-in or refresh.'}
                </span>
            </div>
        </div>
    )
}

export default OrgPagesCard
